import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Button from '../Custom-UI/Button.jsx';
import { blue, red, green } from '@mui/material/colors';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { keyframes } from '@mui/system';
import ReactMarkdown from 'react-markdown';
import { cn } from '../../utils/cn.js';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import api from '../../api/axios';
import authorizationHeader from '../../api/token';

const borderPulse = keyframes`
  0% {
    box-shadow: 0 0 0 2px rgba(0, 69, 145, 0.6);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(0, 69, 145, 0);
  }
  100% {
    box-shadow: 0 0 0 2px rgba(0, 69, 145, 0.6);
  }
`;

const ActionButton = ({
  handleAction,
  actionLoading,
  actionSuccess,
}) => {
  return (
    <Button
      size="small"
      variant="contained"
      onClick={handleAction}
      disabled={actionLoading || actionSuccess}
      startIcon={<PlayArrowIcon />}
      sx={{
        minWidth: 'auto',
        px: 2,
        py: 0.5,
        fontSize: '0.75rem',
        fontWeight: 'bold',
        borderRadius: 2,
        animation: !actionLoading && !actionSuccess && `${borderPulse} 2s ease-in-out infinite`,
        backgroundColor: actionSuccess ? green[600] : undefined,
        '&:hover': {
          backgroundColor: actionSuccess ? green[700] : undefined,
        },
      }}
      title={"Confirmar"}
    >
      {actionLoading ? 'Ejecutando...' : actionSuccess ? 'Completado!' : 'Confirmar'}
    </Button>
  )
}

const MessageComponent = ({
  messageId,
  chatId,
  content,
  timestamp,
  hasAction,
  actionResult,
  isUser = false,
  setNewMessage,
}) => {
  const [liked, setLiked] = useState(null); // null, 'up', 'down'
  const [copied, setCopied] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(!(actionResult?.success ?? false) ? actionResult?.message : null);
  const [actionSuccess, setActionSuccess] = useState((actionResult?.success ?? false) ? actionResult?.message : null);

  const handleLike = (type) => {
    if (isUser) return; // Users can't like their own messages
    setLiked(liked === type ? null : type);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('Failed to copy text: ', err);
    }
  };

  const handleAction = async () => {
    setActionLoading(true);
    setActionError(null);

    try {
      const token = await authorizationHeader();
      const response = await api.post(`/MediBot/teachers/run-action`, {
        chat_id: chatId,
        message_id: messageId,
      }, { headers: token });
      const responseData = response.data;
      const { result } = responseData;
      if ((result?.success ?? false)) {
        setActionSuccess(result.message);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setActionError(error.message || 'Error al ejecutar la acción');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isUser) {
    return (
      <div className="flex items-end justify-end gap-3 mb-4">
        <div className="flex flex-col items-end max-w-[70%]">
          <div className="bg-primary text-white p-3 rounded-lg rounded-tr-none shadow-sm">
            <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              size="small"
              onClick={() => setNewMessage(content)}
              className="text-gray-500 hover:text-blue-600"
              title="Repetir"
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
            <span className="text-xs text-gray-500 mt-1">
              {formatTimestamp(timestamp)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="flex flex-col max-w-[70%]">
        <div className="bg-gray-100 p-3 rounded-lg rounded-tl-none shadow-sm">
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
          {hasAction && (
            <div className="mt-2">
              <div className="flex justify-end">
                <ActionButton
                  handleAction={handleAction}
                  actionLoading={actionLoading}
                  actionSuccess={actionSuccess}
                />
              </div>
              {actionError && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <span className="font-semibold">Error:</span>
                    {actionError}
                  </p>
                </div>
              )}
              {actionSuccess && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-700">
                    <span className="font-semibold">¡Éxito!</span>
                    <span className="block mt-1">
                      {actionSuccess}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-500">
            {formatTimestamp(timestamp)}
          </span>

          <div className="flex items-center gap-1">
            {/* Copy button */}
            <IconButton
              size="small"
              onClick={handleCopy}
              className={cn(
                "transition-colors",
                copied ? "text-green-600" : "text-gray-500 hover:text-gray-700"
              )}
              title={copied ? "Copied!" : "Copy message"}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>

            {/* Like buttons */}
            <IconButton
              size="small"
              onClick={() => handleLike('up')}
              className="text-gray-500 hover:text-green-600"
              title="Like"
            >
              {liked === 'up' ? (
                <ThumbUpIcon fontSize="small" sx={{ color: green[600] }} />
              ) : (
                <ThumbUpOutlinedIcon fontSize="small" />
              )}
            </IconButton>

            <IconButton
              size="small"
              onClick={() => handleLike('down')}
              className="text-gray-500 hover:text-red-600"
              title="Dislike"
            >
              {liked === 'down' ? (
                <ThumbDownIcon fontSize="small" sx={{ color: red[600] }} />
              ) : (
                <ThumbDownOutlinedIcon fontSize="small" />
              )}
            </IconButton>

            <IconButton
              size="small"
              onClick={() => setPinned(!pinned)}
              className="text-gray-500 hover:text-blue-600"
              title="Pin"
            >
              {pinned ? <PushPinIcon fontSize="small" sx={{ color: blue[600] }} /> : <PushPinOutlinedIcon fontSize="small" />}
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary rerenders
export const Message = React.memo(MessageComponent);